// The video record's de facto schema, declared once.
//
// There is no schema on the server -- `api/addupdate.js` merges whatever it is
// sent -- so this file is the admin's own contract, derived from what the
// front-end actually reads. It drives the Overview form, the new-video dialog
// and the "other fields" section, so a field is described here exactly once
// instead of being hand-written into three separate forms.

import { readBool, readOptOut, parseProducts } from '../ps/format';

// Fields the generic editor must never touch: they are owned by the uploader,
// the pipelines and the croppers, and hand-editing them corrupts a video.
export const SYSTEM_FIELDS = new Set([
  'identifier',
  'assets',
  'verticalAssets',
  'subtitles',
  'chapters',
  'thumbnailUrl',
  'minithumbnailUrl',
  'verticalThumbnailUrl',
  'derivedStale',
  'subtitlesStale',
  'chaptersStale',
  // Owned by the Finished control in the detail header, not the generic editor
  // -- two toggles for one field is worse than none.
  'finished',
]);

// The completeness checklist, in the order the work happens.
//
// Both the rail's LED row and the computed `ready` derive from this one list.
// They were hand-maintained separately at first and immediately drifted: the
// lamps showed "live" (which `ready` ignored) while `ready` required products
// (which had no lamp), so a video could show every lamp lit and still be
// filtered as unfinished.
//
// Chapters are deliberately absent. The chapter pipeline itself refuses videos
// under ~96 seconds, so requiring them would leave every quick tip permanently
// red -- and a checklist that can't be satisfied is one you learn to ignore.
export const CHECKLIST = [
  { key: 'video', label: 'Video file' },
  { key: 'poster', label: 'Poster image (player and hero)' },
  { key: 'card', label: 'Card crop (the home page grid)' },
  { key: 'subtitles', label: 'Subtitle tracks' },
  { key: 'products', label: 'Reachable — has products, or is public' },
];

// Replacing the file invalidates the subtitles and the chapters together, but
// they are regenerated separately -- so they need separate flags. One shared
// flag could never be cleared without lying about the other half, which left
// the warning banners on forever.
//
// `derivedStale` is the original single flag; records written before the split
// still carry it, so it is read as "both".
export function staleFlags(video) {
  const legacy = !!video?.derivedStale;
  return {
    subtitles: video?.subtitlesStale ?? legacy,
    chapters: video?.chaptersStale ?? legacy,
  };
}

// Writes the whole trio so the legacy field is retired rather than left to
// contradict the new ones.
export function stalePatch({ subtitles, chapters }) {
  return { subtitlesStale: !!subtitles, chaptersStale: !!chapters, derivedStale: false };
}

// The five tracks a complete video carries. English is the source the pipeline
// corrects; the other four are translated from it, which is why a video with
// only English is a job half done rather than a different kind of video.
export const SUBTITLE_LANGUAGES = ['en', 'de', 'fr', 'it', 'es'];

export const SUBTITLE_LABELS = {
  en: 'English',
  de: 'German',
  fr: 'French',
  it: 'Italian',
  es: 'Spanish',
};

// What this video has, and what it is still missing. A track with no `src` is
// counted as absent: it renders nothing in the player, so treating it as
// present would hide exactly the gap this is here to find.
export function subtitleCoverage(video) {
  const tracks = (Array.isArray(video?.subtitles) ? video.subtitles : []).filter((t) => t?.src);
  const present = new Set(tracks.map((t) => t.srclang));
  return {
    source: tracks.find((t) => t.srclang === 'en') || null,
    present: SUBTITLE_LANGUAGES.filter((lang) => present.has(lang)),
    missing: SUBTITLE_LANGUAGES.filter((lang) => !present.has(lang)),
  };
}

// Never allowed into client state or a write, whatever claims to have set it.
// `password` is here because Previewplayer used to hand its request body back
// up as if it were the record, which then rendered as an editable field and
// got persisted on the next save.
export const FORBIDDEN_FIELDS = new Set(['password']);

export const FIELDS = [
  {
    key: 'title',
    label: 'Title',
    type: 'text',
    help: 'Shown on the cards, the player heading and the chat context.',
  },
  {
    key: 'description',
    label: 'Description',
    type: 'longtext',
    help: 'Card hover text and the paragraph under the player.',
  },
  {
    key: 'category',
    label: 'Category',
    type: 'category',
    help: 'Groups the video into a row on the home page. Matched exactly, so casing matters.',
  },
  {
    key: 'products',
    label: 'Products',
    type: 'products',
    help: 'Owning any one of these unlocks the video. Matched against product names exactly.',
  },
  {
    key: 'frontend',
    label: 'Live on home',
    type: 'flag',
    help: 'The only thing that puts this video on the home page.',
  },
  {
    key: 'public',
    label: 'Public',
    type: 'flag',
    help: 'Unlocks the video for everyone, ignoring products and subscription.',
  },
  {
    key: 'chatbot',
    label: 'Video chat',
    type: 'optout',
    help: 'On by default wherever an English subtitle track exists. Turn off to hide the chat button.',
  },
  {
    key: 'linktext',
    label: 'Link text',
    type: 'text',
    help: 'Label of the call-to-action. Only rendered together with a link.',
  },
  {
    key: 'link',
    label: 'Link URL',
    type: 'text',
    help: 'Only reachable on /watch with ?promo=true, or when the viewer does not own the video.',
  },
];

export const FIELD_BY_KEY = Object.fromEntries(FIELDS.map((f) => [f.key, f]));

// A blank record for the new-video dialog, built from the declarations rather
// than a hand-maintained literal. An opt-out starts ON -- starting it off would
// write `chatbot: 'false'` into every new video and silently disable chat.
export function emptyFields() {
  return Object.fromEntries(
    FIELDS.map((f) => {
      if (f.type === 'optout') return [f.key, true];
      if (f.type === 'flag') return [f.key, false];
      // A multi-value field has to start as an array: MUI's multiple
      // Autocomplete calls .some() on its value the moment the popup opens, so
      // a blank string blanks the whole page on the first click.
      if (f.type === 'products') return [f.key, []];
      return [f.key, ''];
    }),
  );
}

// Keys on a record that neither the schema nor the system owns -- the ad-hoc
// fields added through "Add field" over the years.
export function extraFieldKeys(video) {
  return Object.keys(video || {}).filter(
    (key) => !FIELD_BY_KEY[key] && !SYSTEM_FIELDS.has(key) && !FORBIDDEN_FIELDS.has(key),
  );
}

// Strips anything that must not enter client state or a write. Everything that
// writes to the store passes through here.
export function sanitize(record) {
  if (!record || typeof record !== 'object') return record;
  const clean = {};
  for (const [key, value] of Object.entries(record)) {
    if (FORBIDDEN_FIELDS.has(key)) continue;
    if (key.startsWith('__')) continue;
    clean[key] = value;
  }
  return clean;
}

// What a video still needs, as flags the rail can render as LEDs.
//
// `ready` means the checklist is satisfied. `finished` means you said so --
// the manual marker wins, because some videos will never satisfy every check
// and still need to leave the worklist.
export function deriveStatus(video) {
  const v = video || {};
  const coverage = subtitleCoverage(v);
  const status = {
    live: readBool(v.frontend),
    public: readBool(v.public),
    finished: readBool(v.finished),
    video: !!(v.assets && (v.assets.hls || v.assets.mp4)),
    poster: !!v.thumbnailUrl,
    // Home.js renders the grid cards from minithumbnailUrl and hides the image
    // on error, so a video without a crop shows no artwork at all.
    card: !!v.minithumbnailUrl,
    // A video is not subtitled until all five tracks exist. Counting "has at
    // least one" as done is what let twenty videos sit in the catalog with
    // every lamp lit and no Italian: the lamp answered a question nobody was
    // asking. `subtitlesPartial` keeps the two failures distinguishable --
    // nothing generated yet reads differently from a run that dropped one
    // language, and they need different work.
    subtitles: coverage.missing.length === 0,
    subtitlesPartial: coverage.present.length > 0 && coverage.missing.length > 0,
    subtitlesPresent: coverage.present.length,
    subtitlesMissing: coverage.missing,
    chapters: (v.chapters || []).length > 0,
    // Chat needs the transcript, not the translations, so it is satisfied by
    // the English track alone whatever the other four are doing.
    chat: readOptOut(v.chatbot) && !!coverage.source,
    stale: staleFlags(v).subtitles || staleFlags(v).chapters,
    products: parseProducts(v.products).length > 0 || readBool(v.public),
  };
  status.ready = CHECKLIST.every(({ key }) => status[key]);
  return status;
}

// Records the site will misread because a flag was stored as a real boolean
// rather than the 'true'/'false' string Home.js compares against.
export function findBrokenFlags(videos) {
  const broken = [];
  for (const [identifier, video] of Object.entries(videos || {})) {
    if (!video || typeof video !== 'object') continue;
    const keys = ['frontend', 'public'].filter((k) => typeof video[k] === 'boolean');
    if (!keys.length) continue;
    // The stored boolean carries the intent even though the site cannot read
    // it, so the repair preserves the value rather than assuming one.
    const values = Object.fromEntries(keys.map((k) => [k, video[k]]));
    broken.push({ identifier, title: video.title || identifier, keys, values });
  }
  return broken;
}

// Records that already have the admin password persisted into them. `sanitize`
// hides it from the UI, but it stays in KV until it is explicitly deleted --
// and `getall` is unauthenticated, so it is world-readable there.
//
// Takes the *raw* getall response, before sanitize strips the evidence.
export function findLeakedPasswords(rawVideos) {
  return Object.entries(rawVideos || {})
    .filter(([, v]) => v && typeof v === 'object' && 'password' in v)
    .map(([identifier, v]) => ({ identifier, title: v.title || identifier }));
}
